import { Component } from 'react';

/**
 * ErrorBoundary - Catches React component errors and provides recovery UI
 *
 * This prevents the entire app from crashing when a component throws an error.
 * Users can retry, refresh, or go back to the home page.
 */
export class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render shows the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Log error details
    console.error('[ErrorBoundary] Caught error:', error.message);
    console.error('[ErrorBoundary] Component stack:', errorInfo.componentStack);

    this.setState({ errorInfo });

    // Store error state for debugging
    try {
      sessionStorage.setItem('gjyl-mobile-last-error', JSON.stringify({
        message: error.message,
        stack: error.stack,
        componentStack: errorInfo.componentStack,
        timestamp: Date.now(),
        url: window.location.href
      }));
    } catch (e) {
      // Ignore storage errors
    }
  }

  handleRetry = () => {
    // Clear error state and try to re-render children
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  handleGoHome = () => {
    // Clear all error states and navigate to home
    try {
      sessionStorage.removeItem('gjyl-mobile-last-error');
      // Clear potentially problematic localStorage
      localStorage.removeItem('gjyl-mobile-view');
      localStorage.removeItem('gjyl-mobile-chat');
    } catch (e) {
      // Ignore storage errors
    }

    // Navigate to home without adding to history stack
    window.location.replace('/');
  };

  handleRefresh = () => {
    // Hard refresh the page
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-gray-800 rounded-2xl p-8 text-center border border-gray-700">
            {/* Icon */}
            <div className="mb-6">
              <div className="w-16 h-16 mx-auto rounded-full bg-red-500/10 flex items-center justify-center">
                <svg className="w-8 h-8 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
            </div>

            {/* Message */}
            <h1 className="text-xl font-semibold text-white mb-2">
              Something went wrong
            </h1>
            <p className="text-gray-400 mb-6 text-sm">
              We encountered an unexpected error. You can try refreshing or go back to the home page.
            </p>

            {/* Error details in development */}
            {import.meta.env.DEV && this.state.error && (
              <details className="mb-6 text-left">
                <summary className="text-sm text-gray-500 cursor-pointer hover:text-gray-400">
                  Error details
                </summary>
                <pre className="mt-2 p-3 bg-black/50 rounded-lg text-xs text-red-400 overflow-auto max-h-32">
                  {this.state.error.message}
                  {this.state.error.stack && `\n\n${this.state.error.stack}`}
                </pre>
              </details>
            )}

            {/* Actions */}
            <div className="flex flex-col gap-3">
              <button
                onClick={this.handleRetry}
                className="w-full py-3 px-4 bg-cyan-500 hover:bg-cyan-400 text-black font-medium rounded-xl transition-colors"
              >
                Try Again
              </button>
              <button
                onClick={this.handleRefresh}
                className="w-full py-3 px-4 bg-gray-700 hover:bg-gray-600 text-white font-medium rounded-xl transition-colors"
              >
                Refresh Page
              </button>
              <button
                onClick={this.handleGoHome}
                className="w-full py-3 px-4 text-gray-400 hover:text-white font-medium transition-colors"
              >
                Go to Home Page
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
